import type { Assistant } from '@@/types/assistant'
import { parseMarkdown } from '@nuxtjs/mdc/runtime'

async function attachMeta(assistant: Assistant) {
  if (assistant.prompt?.startsWith('---')) {
    try {
      const { data } = await parseMarkdown(assistant?.prompt)
      // Merge frontmatter into canonical metadata without overwriting DB-provided keys
      const existing = assistant.metadata || {}
      assistant.metadata = { ...data, ...existing }
    } catch (e) {
      console.warn("Error parsing assistant meta: ", assistant.id)
    }
  }

  return assistant
}

// Helper functions for Lucide icon handling
const isLucideIcon = (iconUrl: string | undefined): boolean => {
  return iconUrl ? iconUrl.startsWith('lucide:') : false
}

// Helper function for Lucide icon name
const getLucideIconName = (iconUrl: string | undefined): string => {
  if (!iconUrl || !iconUrl.startsWith('lucide:')) return ''
  const parts = iconUrl.split(':')
  return parts[1] ? `i-lucide-${parts[1]}` : 'i-lucide-user'
}

// Helper function for Lucide icon color
const getLucideIconColor = (iconUrl: string | undefined): string => {
  if (!iconUrl || !iconUrl.startsWith('lucide:')) return ''
  const parts = iconUrl.split(':')
  return parts[2] || 'text-blue-600'
}


export const useAssistants = () => {
  // Back-compat combined list (DB + Vapi)
  const assistants = useState<Assistant[]>('assistants', () => [])

  // DB assistants categorized (owned, editable, read, chat, collaborative, visible, all)
  const dbAssistants = useState<{
    owned?: Assistant[]
    editable?: Assistant[]
    read?: Assistant[]
    chat?: Assistant[]
    collaborative?: Assistant[]
    visible?: Assistant[]
    all?: Assistant[]
  }>('dbAssistants', () => ({}))

  // Vapi assistants (flat)
  const vapiAssistants = useState<Assistant[]>('vapiAssistants', () => [])
  const selectedAssistant = useState<Assistant | null>('selectedAssistant', () => null)
  const isLoading = useState('assistantsIsLoading', () => false)

  const fetchAssistants = async () => {
    // Avoid refetch if we already have both caches populated
    if ((assistants.value?.length || 0) > 0 && vapiAssistants.value.length > 0) {
      return assistants.value
    }

    isLoading.value = true
    try {
      // Fetch DB (categorized) and Vapi (flat) in parallel
      const [dbRes, vapiRes] = await Promise.all([
        useFetch('/api/assistants/list', { server: false, deep: true }),
        useFetch('/api/assistants', { server: false, deep: true })
      ])

      // Handle DB categorized payload
      const dbPayload = (dbRes.data.value || {}) as unknown as {
        owned?: Assistant[]
        editable?: Assistant[]
        read?: Assistant[]
        chat?: Assistant[]
        collaborative?: Assistant[]
        visible?: Assistant[]
        all?: Assistant[]
      }
      dbAssistants.value = dbPayload

      // Choose a primary DB list for combined view (fallbacks for non-admins)
      const dbPrimary: Assistant[] = (dbPayload.chat && dbPayload.chat.length > 0)
        ? dbPayload.chat
        : (dbPayload.all && dbPayload.all.length > 0)
          ? dbPayload.all
          : (dbPayload.read && dbPayload.read.length > 0)
            ? dbPayload.read
            : (dbPayload.visible && dbPayload.visible.length > 0)
              ? dbPayload.visible
              : ([...(dbPayload.owned || []), ...(dbPayload.collaborative || [])])

      // Vapi flat list
      const vapiListRaw = (vapiRes.data.value as unknown as Assistant[]) || []
      const vapiWithMeta = (await Promise.all(vapiListRaw.map(attachMeta))).filter(Boolean) as Assistant[]
      vapiAssistants.value = vapiWithMeta

      // Combine DB + Vapi for back-compat usages
      const combinedRaw = [...dbPrimary, ...vapiWithMeta]
      const seen = new Set<string>()
      const uniqueCombined = combinedRaw.filter(a => {
        if (!a?.id || seen.has(a.id)) return false
        seen.add(a.id)
        return true
      })
      const withMeta = (await Promise.all(uniqueCombined.map(attachMeta))).filter(Boolean) as Assistant[]
      assistants.value = withMeta

      return assistants.value
    } finally {
      isLoading.value = false
    }
  }

  const getAssistantById = (id: string) => {
    return assistants.value.find((assistant) => assistant.id === id)
  }

  const refreshAssistants = async () => {
    // Force refresh by clearing cache first
    assistants.value = []
    dbAssistants.value = {}
    vapiAssistants.value = []
    return await fetchAssistants()
  }

  return {
    assistants,
    dbAssistants,
    vapiAssistants,
    selectedAssistant,
    isLoading,
    fetchAssistants,
    refreshAssistants,
    getAssistantById,
    isLucideIcon,
    getLucideIconName,
    getLucideIconColor,
  }
}


